import * as React from 'react';
import { SpirometryAnalysisSetTypeEnum } from "../../../misc/api/ApiSpirometryAnalysisSetType"
import { Link } from "react-router-dom";
import { Column, Table } from "../../../Components/QueryTable";
import { MatchSortHeader } from "../../../Components/MatchSortHeader";
import { DateMatchSortHeader } from "../../../Components/DateMatchSortHeader";
import { WebPortalClient, SvcAnalysisSet } from "webportalclient";
import { formatDateTime } from "../../../misc/components/Formatters/Formatters";
import uniqBy from "lodash-es/uniqBy";
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';
import { HamburgerMenu } from '../../../misc/components/HamburgerMenu';
import { Authorization } from "../../../misc/components/Authorization";
import { getAnalysisSetsQcBlindingFilter, useQuery } from "../../../Components/Query";
import Condition from "../../../misc/components/Condition";
import { Permission, OverreadStatusIdEnum, User, ChallengeDecisionMaker, OverreadChallengeStatusEnum } from "../../../misc/api/apiTypes";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from '../../../misc/api/ApiSessionTypes';
import { CreateAnalysisBatchModal } from '../../QC/CreateAnalysisBatchModal';
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { AnalysisAcceptabilityStatus } from "../../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { AssignToUserInGroupModal } from '../../../misc/components/AssignToUserInGroupModal';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';
import { ChallengeStatus, ChallengeStatusCoded } from '../../Shared/GetAnalysisStatus';

export interface SvcAnalysisListTableProps {
}

export const SvcAnalysisSetListTable = (props: SvcAnalysisListTableProps) => {
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [analystNames, setAnalystNames] = React.useState([]);
    const { data, applyFilters, filters, refresh } = useQuery();
    const client = useWebPortalApiClient();
    const [legacyClient] = React.useState(useWebPortalLegacyClient())
    const { study } = useCurrentStudyConfigurationContext();
    const [isLoading, setIsLoading] = React.useState(false);
    const [overreadEnabled, setOverreadEnabled] = React.useState(false);
    const [overreadChallengeProtocol, setOverreadChallengeProtocol] = React.useState<ChallengeDecisionMaker>();

    const handleReassign = async (analysisSetId: string, userName: string) => {

        const analysisSetForReassign = await client.client.getSvcAnalysisSet(analysisSetId);
        analysisSetForReassign.analystUserName = userName;

        await client.client.patchSvcAnalysisSet(analysisSetForReassign, analysisSetId);

        setReassignToUserModalVisible(false);
        refresh();
    }

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getSvcSessions(undefined, `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($expand=subject($expand=site))");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getSvcSessions('name', `visit/subject/site/studyId eq ${study.id}`, 'name', "visit($select=subject;$expand=subject($select=site;$expand=site))");
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getSvcAnalysisSets("analystUserId,analystUserName", `session/visit/subject/site/studyId eq ${study.id}`, undefined);

            const options = uniqBy(analysts.value, o => o.analystUserId).filter(o => o.analystUserId).map(s => ({
                value: s.analystUserId,
                label: s.analystUserName,
            }));

            setAnalysts(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await client.client.getSvcAnalysisSetTypes();
            const options = uniqBy(types.value, o => o.id).map(s => ({
                value: s.externalCode,
                label: s.name,
            }));

            setAnalystSetTypes(options);
        };

        const applyStudySettings = async () => {
            const svcSettings = await legacyClient.getStudySvcSettings();
            setOverreadEnabled(svcSettings.IsOverreadingAvailable);
        }

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalystSetTypes();
        getAnalysts();
        applyStudySettings();

    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const isQcAnalysisSet = (set: SvcAnalysisSet): boolean => set.type === SpirometryAnalysisSetTypeEnum.QC;

    const formatSiteId = set => isQcAnalysisSet(set) ? '-' : set.session.visit.subject.site.number;
    const formatSubjectId = set => isQcAnalysisSet(set) ? '-' : set.session.visit.subject.number;
    const formatVisitLabel = set => isQcAnalysisSet(set) ? '-' : set.session.visit.name;
    const formatSessionLabel = set => isQcAnalysisSet(set) ? '-' : set.session.name;
    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (overreadAnalysisSet: SvcAnalysisSet) => {
        if (!overreadAnalysisSet.session.challengeIsClosed && overreadAnalysisSet.session.overreadChallengeStatus === OverreadChallengeStatusEnum.Challenged && (overreadAnalysisSet.status === OverreadStatusIdEnum.Accepted || overreadAnalysisSet.status === OverreadStatusIdEnum.Rejected)) {
            return <ChallengeStatusCoded status={overreadAnalysisSet.status} challengeStatus={overreadAnalysisSet.session.overreadChallengeStatus} />;
        }
    
        return (<>
            <AnalysisAcceptabilityStatus value={overreadAnalysisSet.status} /><br />
        </>);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const formatAnalystSetType = (type: string) => {
        switch (type) {
            case SpirometryAnalysisSetTypeEnum.Overread:
                return "Overread";
            case SpirometryAnalysisSetTypeEnum.Site:
                return "Site";
            case SpirometryAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const refreshData = () => {
        setIsLoading(true);

        refresh();

        legacyClient.getStudySvcSettings().then(stSvcS => {
            setOverreadEnabled(stSvcS && stSvcS.IsOverreadingAvailable);
            setOverreadChallengeProtocol(stSvcS.SelectedFinalTestAcceptabilityDecisionMaker);
            setIsLoading(false);
        });
    }
    
    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createSvcQC(sessionId, SpirometryAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refreshData();
    }

    const [isAssignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <i className="fa fa-check icon"></i>
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <i className="fa fa-times icon"></i>
                        There was an error. QC batch wasn't saved.
                    </div> 
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const RenderHamburgerMenu = (analysisSet: SvcAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User, overreadEnabled: boolean) => {
        return (
            <HamburgerMenu automationId={`options_${analysisSet.id}`}>
                <Authorization permissionId={PermissionEnum.AccessSingleSvcSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <Condition test={() => (analysisSet.type !== SpirometryAnalysisSetTypeEnum.QC) || (analysisSet.type === SpirometryAnalysisSetTypeEnum.QC && currentUser.Username === analysisSet.analystUserName)}>
                    <li>
                        <Link data-automationid={`ViewLink_${analysisSet.id}`} to={`/Svc/Analysis/${analysisSet.id}/view`}>View</Link>
                    </li>
                    </Condition>
                </Authorization>
                <Authorization permissionIdsAll={[PermissionEnum.OverreadSvcSession]}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    {overreadEnabled && (!analysisSet || !analysisSet.analystUserName || analysisSet.analystUserName === currentUser.Username) ?
                        <Condition test={() => analysisSet !== null
                            && analysisSet.type !== SpirometryAnalysisSetTypeEnum.Site}>
                            <li>
                                <Link data-automationid={`OverreadLink_${analysisSet.id}`} to={`/Svc/Analysis/${analysisSet.id}/overread`}>Over-read</Link>
                            </li>
                        </Condition>
                        :null}
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateAQCSvcAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(analysisSet.sessionId); }}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadSvcSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread && analysisSet.status === OverreadStatusIdEnum.Pending}>
                        <li>
                            <a data-automationid={`ReassignLink_${analysisSet.id}`} onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>
                                Reassign
                            </a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">SVC Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button
                            className="btn btn-primary"
                            data-automationid="svc-analysis-sets-clear-filters"
                            onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                        
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQCSvcSelection}>
                            &nbsp;
                            <button className="btn btn-primary" onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                        </Authorization>
                    </div>
                </div>
            </div>
            {handleQcBatchSavingAlertMessage()}
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content" data-automationid="svcAnalysisTable">
                            <Table data={data}>
                                <Column field="" header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Site" />} formatter={set => formatSiteId(set)} />
                                <Column field="" header={<MatchSortHeader title="Subject ID" field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Subject" />} formatter={set => formatSubjectId(set)} />
                                <Column field="" header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Visit" />} formatter={set => formatVisitLabel(set)} />
                                <Column field="" header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Session" />} formatter={set => formatSessionLabel(set)} />
                                <Column field="type" header={<MatchSortHeader title="Analyst Set Type" field="type" options={analystSetTypes} sortField="type" automationId="AnalysisSetType" />} formatter={type => formatAnalystSetType(type)} />
                                <Column field="created" header={<DateMatchSortHeader title="Created At" field="created" sortField="created" automationId="CreatedAt" />} formatter={created => formatDateTime(created)} />
                                <Column field="" header={<DateMatchSortHeader title="Started At" field="started" sortField="started" automationId="StartedAt" />} formatter={session => formatDateTime(session.started)} />
                                <Column field="analystUserName" header={<MatchSortHeader title="Overreader" field="analystUserId" options={analysts} sortField="analystUserName" automationId="Overreader" />} formatter={analyst => formatAnalyst(analyst)} />
                                <Column field="" header={<MatchSortHeader title="Status" field="status" options={statusesOptions} sortField="status" automationId="Status" />} formatter={analysisSet => formatStatus(analysisSet)} />
                                <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id, currentUser, overreadEnabled)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Svc} minimumTests={3} onSave={(result: boolean) => { setQcBatchSaved(result); refreshData(); }} onClose={() => setQCSelectionModelVisible(false)} /> : null}

            <AssignToUserInGroupModal show={isAssignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadSvcSession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadSvcSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user, userName) => { handleReassign(sessionIdToReassign, userName) }} />
        </>
    );

}

export default SvcAnalysisSetListTable;
